'use client';

import { ChevronRightIcon } from '@heroicons/react/24/solid';
import {
  Carousel,
  type CarouselApi,
  CarouselContent,
  CarouselDot,
  CarouselDotCard,
  CarouselItem,
  useCarouselDots,
} from '@vs-shop/ui/carousel';
import { clsxm } from '@vs-shop/utils';
import Autoplay from 'embla-carousel-autoplay';
import { useState } from 'react';
import { useIntl } from 'react-intl';

import { Url } from '../../../../route/router';

import Link from '@vs-shop/image-link/Link';
import HomepageArticlePreview from '../../../server/page/homepage/articles/HomepageArticlePreview';

import type { HomepageArticle } from '../../../../effect/schema/openapi/backend';

type Props = {
  articles: readonly HomepageArticle[];
};

export default function HomepageArticlesCarousel({ articles }: Props) {
  const intl = useIntl();
  const [api, setApi] = useState<CarouselApi>();
  const { selectedIndex, scrollSnaps, onDotButtonClick } = useCarouselDots(api);

  return (
    <div className="@container grid h-60 w-full pho:h-[366px] des:h-[533px]">
      <Carousel
        setApi={setApi}
        plugins={[Autoplay({ delay: 5000 })]}
        className="flex w-full flex-col tab:grid tab:grid-cols-[1fr_300px]"
        data-slot="homepage-articles-carousel"
      >
        {/* left */}
        <CarouselContent className="-ml-0">
          {articles.map((item) => {
            return (
              <CarouselItem key={item.id} className="pl-0">
                <HomepageArticlePreview article={item} />
              </CarouselItem>
            );
          })}
        </CarouselContent>

        {/* dots — mobile */}
        <div className="relative -bottom-4 flex items-center justify-center gap-x-2 tab:hidden">
          {scrollSnaps.map((_, index) => (
            <CarouselDot
              key={index}
              variant="bar"
              current={index === selectedIndex}
              onClick={() => {
                onDotButtonClick(index);
              }}
            />
          ))}
        </div>

        {/* nav cards — desktop (max three) */}
        <div className="hidden h-60 grid-rows-4 divide-y divide-background/20 bg-background-accent-muted pho:h-[366px] tab:grid des:h-[533px]">
          {scrollSnaps.slice(0, 3).map((_, index) => (
            <CarouselDotCard
              key={index}
              current={index === selectedIndex}
              onMouseEnter={() => {
                onDotButtonClick(index);
              }}
              heading={articles[index]?.title ?? ''}
              category={articles[index]?.section.name ?? ''}
            />
          ))}

          <Link
            href={Url.articles}
            data-slot="homepage-articles-view-all"
            className="group grid hover:bg-primary"
          >
            <div className="flex items-center justify-between px-3 des:px-6">
              <span
                className={clsxm(
                  'font-heading',
                  'max-w-32 group-hover:text-foreground group-hover:underline'
                )}
              >
                {intl.formatMessage({
                  id: 'component.client.page.homepage.homepageArticlesCarousel.articles',
                  defaultMessage: 'Zobrazit všechny články',
                })}
              </span>
              <div className="flex size-8 items-center justify-center rounded-full bg-background group-hover:bg-background-inverted des:size-12">
                <ChevronRightIcon className="size-4 shrink-0 text-foreground group-hover:text-foreground-inverted des:size-5" />
              </div>
            </div>
          </Link>
        </div>
      </Carousel>
    </div>
  );
}
